import React, { useMemo, useRef } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { CssBaseline, IconButton } from '@mui/material';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { SnackbarProvider } from 'notistack';
import { ReactQueryWrapper } from 'ReactQueryWrapper';

import { BASE_URL } from 'common/baseUrl';

import { CloseIcon } from 'icons';

import { configureTheme } from './configureTheme';

function getNonce(): string | undefined {
    const el = document.querySelector<HTMLElement>('[data-nonce]');
    return el?.dataset?.nonce;
}

const cache = createCache({
    key: 'css',
    nonce: getNonce(),
});

export function AppEnvironment({ children }) {
    // add action to all snackbars
    const notistackRef = useRef<SnackbarProvider>(undefined);
    const onClickDismiss = key => () => {
        notistackRef.current.closeSnackbar(key);
    };

    const router = useMemo(
        () =>
            createBrowserRouter(
                [
                    {
                        path: '*',
                        element: <ReactQueryWrapper>{children}</ReactQueryWrapper>,
                    },
                ],
                { basename: `/${BASE_URL}` }
            ),
        [children]
    );

    return (
        <StyledEngineProvider injectFirst>
            <CacheProvider value={cache}>
                <ThemeProvider theme={configureTheme()}>
                    <SnackbarProvider
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                        ref={notistackRef}
                        action={key => (
                            <IconButton color="inherit" onClick={onClickDismiss(key)} size="large">
                                <CloseIcon />
                            </IconButton>
                        )}
                    >
                        <CssBaseline />
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <RouterProvider router={router} />
                        </LocalizationProvider>
                    </SnackbarProvider>
                </ThemeProvider>
            </CacheProvider>
        </StyledEngineProvider>
    );
}
